import { ref, computed } from 'vue';
import api from '@/services/api';

const alerts = ref([]);
const currentAlert = ref(null);
const loading = ref(false);
const error = ref(null);
const pagination = ref({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
});

export function usePatientAlerts() {
    // Configuración
    const API_ENDPOINT = '/patient-alerts';

    /**
     * Listar alertas con filtros y paginación
     */
    const fetchAlerts = async (params = {}) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.get(API_ENDPOINT, {
                params: {
                    search: params.search || '',
                    patient_id: params.patient_id || null,
                    per_page: params.per_page || 10,
                    page: params.page || 1,
                    sort_column: params.sort_column || 'created_at',
                    sort_order: params.sort_order || 'desc'
                }
            });

            alerts.value = response.data.data;
            
            if (response.data.meta) {
                pagination.value = {
                    current_page: response.data.meta.current_page,
                    last_page: response.data.meta.last_page,
                    per_page: response.data.meta.per_page,
                    total: response.data.meta.total
                };
            }

            return response.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al cargar alertas';
            console.error('Error fetching alerts:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Obtener una alerta específica
     */
    const fetchAlert = async (id) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.get(`${API_ENDPOINT}/${id}`);
            currentAlert.value = response.data.data;
            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al cargar la alerta';
            console.error('Error fetching alert:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Crear nueva alerta
     */
    const createAlert = async (alertData) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.post(API_ENDPOINT, {
                patient_id: alertData.patient_id,
                title: alertData.title,
                description: alertData.description
            });

            // Agregar a la lista local
            alerts.value.unshift(response.data.data);
            
            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al crear la alerta';
            console.error('Error creating alert:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Actualizar alerta existente
     */
    const updateAlert = async (id, alertData) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.put(`${API_ENDPOINT}/${id}`, alertData);

            // Actualizar en la lista local
            const index = alerts.value.findIndex(a => a.id === id);
            if (index !== -1) {
                alerts.value[index] = response.data.data;
            }

            if (currentAlert.value?.id === id) {
                currentAlert.value = response.data.data;
            }

            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al actualizar la alerta';
            console.error('Error updating alert:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Eliminar alerta
     */
    const deleteAlert = async (id) => {
        loading.value = true;
        error.value = null;
        
        try {
            await api.delete(`${API_ENDPOINT}/${id}`);

            // Remover de la lista local
            alerts.value = alerts.value.filter(a => a.id !== id);

            if (currentAlert.value?.id === id) {
                currentAlert.value = null;
            }

            return true;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al eliminar la alerta';
            console.error('Error deleting alert:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Restaurar alerta eliminada
     */
    const restoreAlert = async (id) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.post(`${API_ENDPOINT}/${id}/restore`);
            
            // Agregar de vuelta a la lista
            alerts.value.unshift(response.data.data);
            
            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al restaurar la alerta';
            console.error('Error restoring alert:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Obtener alertas por paciente
     */
    const fetchAlertsByPatient = async (patientId, params = {}) => {
        return await fetchAlerts({
            ...params,
            patient_id: patientId
        });
    };

    /**
     * Limpiar estado
     */
    const clearAlerts = () => {
        alerts.value = [];
        currentAlert.value = null;
        error.value = null;
    };

    // Computed
    const hasAlerts = computed(() => alerts.value.length > 0);
    const alertsCount = computed(() => pagination.value.total);

    return {
        // State
        alerts,
        currentAlert,
        loading,
        error,
        pagination,

        // Computed
        hasAlerts,
        alertsCount,

        // Methods
        fetchAlerts,
        fetchAlert,
        createAlert,
        updateAlert,
        deleteAlert,
        restoreAlert,
        fetchAlertsByPatient,
        clearAlerts
    };
}
